import React, { useState } from "react";
import { useData } from "@/context/DataContext";
import { PageTitle, Fab, RowActions, EmptyState } from "@/components/shared";
import DataDialog from "@/components/DataDialog";
import PhotoGalleryModal from "@/components/PhotoGalleryModal";
import { formatRupiah } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ImageIcon, StickyNote } from "lucide-react";
import { toast } from "sonner";

export default function Vendor() {
  const { data, create, update, remove } = useData();
  const items = data.vendor || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [galleryFor, setGalleryFor] = useState(null);

  const sorted = [...items].sort((a, b) => (a.vendor || "").localeCompare(b.vendor || ""));

  const fields = [
    { name: "vendor", label: "Jenis Vendor (mis. Catering)", type: "text", required: true },
    { name: "nama_vendor", label: "Nama Vendor", type: "text", required: true },
    { name: "nama_kontak", label: "Nama Kontak", type: "text" },
    { name: "quotation", label: "Quotation", type: "currency" },
    { name: "no_kontak", label: "Nomor Kontak", type: "text" },
    { name: "bank", label: "Bank", type: "text" },
    { name: "no_rekening", label: "Nomor Rekening", type: "text" },
    { name: "notes", label: "Notes", type: "textarea", placeholder: "Catatan tambahan (mis. paket termasuk, syarat, waktu operasional...)" },
    { name: "photos", label: "Upload Foto", type: "photos", max: 10 },
    { name: "dipakai", label: "Akan dipakai", type: "checkbox" },
  ];

  const onSave = async (payload) => {
    if (editing) { await update("vendor", editing.id, payload); toast.success("Vendor diperbarui"); }
    else { await create("vendor", payload); toast.success("Vendor ditambahkan"); }
  };

  const openGallery = (v) => {
    const photos = Array.isArray(v.photos) ? v.photos.filter(Boolean) : [];
    if (photos.length === 0) return;
    setGalleryFor({ title: `Foto — ${v.nama_vendor}`, photos });
  };

  return (
    <div>
      <PageTitle title="Kontak Vendor" subtitle="Diurutkan berdasarkan jenis vendor" />
      <div className="rounded-xl border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Vendor</TableHead><TableHead>Nama Vendor</TableHead><TableHead>Kontak</TableHead>
            <TableHead className="text-right">Quotation</TableHead><TableHead>Bank / Rek</TableHead>
            <TableHead>Info</TableHead>
            <TableHead>Dipakai</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sorted.map((i) => {
              const photos = Array.isArray(i.photos) ? i.photos.filter(Boolean) : [];
              return (
                <TableRow key={i.id} data-testid={`row-${i.id}`}>
                  <TableCell className="font-semibold">{i.vendor}</TableCell>
                  <TableCell>{i.nama_vendor}</TableCell>
                  <TableCell>{i.nama_kontak || "-"}<br /><span className="text-xs text-muted-foreground">{i.no_kontak}</span></TableCell>
                  <TableCell className="text-right">{i.quotation ? formatRupiah(i.quotation) : "-"}</TableCell>
                  <TableCell className="text-xs">{i.bank || "-"}<br />{i.no_rekening || "-"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {i.notes && (
                        <span data-testid={`vendor-notes-icon-${i.id}`} title={i.notes} className="inline-flex items-center rounded-full border border-border p-1 text-muted-foreground">
                          <StickyNote className="h-3 w-3" />
                        </span>
                      )}
                      {photos.length > 0 && (
                        <Button
                          data-testid={`vendor-view-photos-${i.id}`}
                          onClick={() => openGallery(i)}
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 rounded-full px-2 text-[11px]"
                          title="Lihat foto"
                        >
                          <ImageIcon className="h-3 w-3" /> {photos.length}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{i.dipakai ? <Badge className="bg-green-600">Ya</Badge> : <Badge variant="outline">Tidak</Badge>}</TableCell>
                  <TableCell><RowActions id={i.id} onEdit={() => { setEditing(i); setOpen(true); }} onDelete={() => remove("vendor", i.id)} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {sorted.length === 0 && <EmptyState />}
      </div>
      <Fab testId="fab-vendor" onClick={() => { setEditing(null); setOpen(true); }} />
      <DataDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Vendor" : "Tambah Vendor"} fields={fields} initial={editing} onSave={onSave} />
      <PhotoGalleryModal
        open={!!galleryFor}
        onOpenChange={(o) => !o && setGalleryFor(null)}
        title={galleryFor?.title}
        photos={galleryFor?.photos || []}
      />
    </div>
  );
}
